import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import { acceptOffer as acceptOfferLocal, getOffersForPlan as getOffersForPlanLocal, rejectOffer as rejectOfferLocal, type Offer } from '@data/offers';
import { getPublicPlanById, getPublicPlanByIdFromAPI, type PublicPlan } from '@data/publicPlans';
import { notifyOfferAccepted, notifyOfferRejected } from '@data/notifications';
import { getOffersForPlan as getOffersForPlanAPI, getOffersForSeeker, acceptOffer as acceptOfferAPI, rejectOffer as rejectOfferAPI, ensureProjectForOffer, type OfferResponse } from '@services/offers';
import { useAuth } from '@services/auth';
import { useAdminViewAs, getAdminEffectiveSeekerIdNum } from '../../lib/adminViewAs';
import { createProjectFromOffer } from '@data/projects';
import BackButton from '../../shared/BackButton';
import { FilterToggle, FiltersPanel } from '@components/filters';
import StarRating from '@components/StarRating';
import ConversationThread from '@components/ConversationThread';
import OfferDetailPreview from '@components/OfferDetailPreview';
import { useI18n } from '../../i18n';

/** One visible offer per gardener per plan (newest wins); also drops duplicate ids. */
export function dedupeOffersForDisplay(list: Offer[]): Offer[] {
	const byGardenerPerPlan = new Map<string, Offer>();
	for (const offer of list) {
		const key = `${offer.planId}|${offer.gardenerId}`;
		const prev = byGardenerPerPlan.get(key);
		if (!prev || new Date(offer.createdAt).getTime() > new Date(prev.createdAt).getTime()) {
			byGardenerPerPlan.set(key, offer);
		}
	}
	return Array.from(byGardenerPerPlan.values()).sort(
		(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
	);
}

// Map backend offer response to frontend Offer format (planId uses db-{id} to match plan cards)
export function mapBackendOfferToFrontend(backendOffer: OfferResponse): Offer {
	// Map status: 'submitted' -> 'pending'
	const statusMap: Record<string, Offer['status']> = {
		'submitted': 'pending',
		'accepted': 'accepted',
		'rejected': 'rejected',
		'draft': 'pending', // Drafts shown as pending
		'withdrawn': 'rejected',
		'expired': 'rejected'
	};

	const raw = backendOffer as { plan_title?: string; plan_location?: string; project_start_date?: string };
	return {
		id: String(backendOffer.id),
		planId: `db-${backendOffer.plan_id}`,
		gardenerId: String(backendOffer.gardener_user_id),
		gardenerName: backendOffer.company_name || backendOffer.gardener_name || 'Unknown Gardener',
		bands: {
			min: backendOffer.price_min || 0,
			mid: backendOffer.price_mid || 0,
			max: backendOffer.price_max || 0
		},
		status: statusMap[backendOffer.status] || 'pending',
		createdAt: backendOffer.created_at,
		moderationStatus: 'active',
		message: backendOffer.public_notes || backendOffer.proposal_text || '',
		timelineEstimate: backendOffer.estimated_timeline || '',
		projectStartDate: raw.project_start_date || (backendOffer as any).project_start_date || undefined,
		validUntil: backendOffer.valid_until || undefined,
		selectedGardeners: backendOffer.selected_gardener_names || [],
		timelineData: backendOffer.timeline_data,
		planSnapshot: backendOffer.plan_snapshot,
		planTitle: raw.plan_title,
		planCity: raw.plan_location
	};
}

export default function OffersForMyPlan() {
	const { planId } = useParams();
	const location = useLocation();
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const planOwnerSeekerIdNum = useMemo(
		() => getAdminEffectiveSeekerIdNum(user, viewAsTarget),
		[user, viewAsTarget]
	);
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const prefetched = (location.state as { prefetchedOffers?: Offer[] } | undefined)?.prefetchedOffers;
	const hasPrefetched = Boolean(prefetched?.length && planId && prefetched.every((o) => o.planId === planId));
	const hadPrefetchedRef = useRef(hasPrefetched);
	hadPrefetchedRef.current = hasPrefetched;
	const [offers, setOffers] = useState<Offer[]>(() => (hasPrefetched ? prefetched! : []));
	const [isLoading, setIsLoading] = useState(!hasPrefetched);
	const [error, setError] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedStatuses, setSelectedStatuses] = useState<Array<Offer['status']>>([]);
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const [expandedOffers, setExpandedOffers] = useState<Set<string>>(new Set());
	const [sortBy, setSortBy] = useState<'newest' | 'price-low' | 'price-high'>('newest');
	const [planForOffers, setPlanForOffers] = useState<PublicPlan | null>(() => (planId ? getPublicPlanById(planId) ?? null : null));
	const [messageOfferId, setMessageOfferId] = useState<string | null>(null);
	const [searchParams] = useSearchParams();
	const resolvedPlanIdNum = useMemo(() => {
		if (!planId) return NaN;
		return planId.startsWith('db-') ? parseInt(planId.slice(3), 10) : parseInt(planId, 10);
	}, [planId]);

		// Expand offer when landing with ?offer_id= (e.g. from notification)
		useEffect(() => {
			const offerIdFromUrl = searchParams.get('offer_id');
			if (!offerIdFromUrl || offers.length === 0) return;
			const hasOffer = offers.some((o) => String(o.id) === offerIdFromUrl);
			if (hasOffer) {
				setExpandedOffers((prev) => new Set(prev).add(offerIdFromUrl));
			}
		}, [searchParams, offers]);

		// Keep plan in state so expanded offer always has plan details (and picture) when no gardener snapshot
		useEffect(() => {
			if (!planId) {
				setPlanForOffers(null);
				return;
			}
			const cached = getPublicPlanById(planId);
			if (cached) {
				setPlanForOffers(cached);
				return;
			}
			getPublicPlanByIdFromAPI(planId).then((p) => {
				if (p) setPlanForOffers(p);
			}).catch(() => {});
		}, [planId]);

		// Load offers from backend API (re-run when user loads so fallback can run)
		useEffect(() => {
			if (!planId) {
				setIsLoading(false);
				return;
			}

			const planIdNum = planId.startsWith('db-') ? parseInt(planId.slice(3), 10) : parseInt(planId, 10);
			const plan = getPublicPlanById(planId);
			const resolvedPlanIdNum = plan?.databaseId ?? (isNaN(planIdNum) ? undefined : planIdNum);
			const id = resolvedPlanIdNum ?? planIdNum;

			const loadOffers = async () => {
				if (!hadPrefetchedRef.current) setIsLoading(true);
				setError(null);

				// Helper: set offers from seeker list filtered by plan (works when user is loaded)
				const setOffersFromSeeker = async () => {
					const sid = planOwnerSeekerIdNum;
					if (sid == null || isNaN(id)) return false;
					const seekerOffers = await getOffersForSeeker(sid);
					const forPlan = seekerOffers.filter((o) => Number(o.plan_id) === id);
					if (forPlan.length > 0) {
						setOffers(dedupeOffersForDisplay(forPlan.map(mapBackendOfferToFrontend)));
						return true;
					}
					return false;
				};

				try {
					if (!isNaN(id)) {
						// 1) Try plan-specific endpoint first (does not depend on user)
						let planOffers: Offer[] = [];
						try {
							const backendOffers = await getOffersForPlanAPI(id);
							planOffers = backendOffers.map(mapBackendOfferToFrontend);
							if (planOffers.length > 0) {
								setOffers(dedupeOffersForDisplay(planOffers));
								return;
							}
						} catch (planApiErr) {
							console.warn('Plan offers API failed, falling back to seeker offers:', planApiErr);
						}
						// 2) Fallback: seeker offers filtered by plan_id (same source as My Plans → Offers)
						if (await setOffersFromSeeker()) return;
						// 3) Use plan API result if we got one (even empty), else localStorage
						if (!hadPrefetchedRef.current) {
							const localOffers = planOffers.length > 0 ? planOffers : getOffersForPlanLocal(planId);
							setOffers(dedupeOffersForDisplay(localOffers));
						}
					} else {
						setOffers(getOffersForPlanLocal(planId));
					}
				} catch (err) {
					console.error('Failed to load offers from API:', err);
					if (await setOffersFromSeeker()) return;
					if (!hadPrefetchedRef.current) {
						try {
							setOffers(getOffersForPlanLocal(planId));
						} catch (localErr) {
							setError('Failed to load offers');
						}
					}
				} finally {
					setIsLoading(false);
				}
			};

			loadOffers();
		
			const handleUpdate = async () => {
				if (!planId) return;
				const num = plan?.databaseId ?? (planId?.startsWith('db-') ? parseInt(planId.slice(3), 10) : parseInt(planId, 10));
				if (!isNaN(num)) {
					try {
						const sid = planOwnerSeekerIdNum;
						if (sid != null) {
							const seekerOffers = await getOffersForSeeker(sid);
							const forPlan = seekerOffers.filter((o) => Number(o.plan_id) === num);
							setOffers(dedupeOffersForDisplay(forPlan.map(mapBackendOfferToFrontend)));
							return;
						}
						const backendOffers = await getOffersForPlanAPI(num);
						setOffers(dedupeOffersForDisplay(backendOffers.map(mapBackendOfferToFrontend)));
					} catch {
						setOffers(getOffersForPlanLocal(planId));
					}
				} else {
					setOffers(getOffersForPlanLocal(planId));
				}
			};
			window.addEventListener('offersUpdated', handleUpdate);

			return () => {
				window.removeEventListener('offersUpdated', handleUpdate);
			};
		}, [planId, user?.id, planOwnerSeekerIdNum]);

	const filteredOffers = useMemo(() => {
		const normalizedSearch = searchTerm.trim().toLowerCase();

		return offers.filter((offer) => {
			const matchesStatus =
				selectedStatuses.length === 0 || selectedStatuses.includes(offer.status);

			const matchesSearch =
				!normalizedSearch ||
				offer.gardenerName.toLowerCase().includes(normalizedSearch) ||
				offer.message?.toLowerCase().includes(normalizedSearch) ||
				offer.timelineEstimate?.toLowerCase().includes(normalizedSearch);

			return (
				matchesStatus &&
				matchesSearch
			);
		}).sort((a, b) => {
			if (sortBy === 'newest') {
				return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
			}
			if (sortBy === 'price-low') return a.bands.mid - b.bands.mid;
			if (sortBy === 'price-high') return b.bands.mid - a.bands.mid;
			return 0;
		});
	}, [
		offers,
		searchTerm,
		selectedStatuses,
		sortBy
	]);

	const activeFiltersCount = useMemo(() => {
		let count = 0;
		count += selectedStatuses.length;
		return count;
	}, [selectedStatuses]);

	const toggleSelection = <T,>(value: T, collection: T[], setCollection: (items: T[]) => void) => {
		setCollection(
			collection.includes(value)
				? collection.filter((item) => item !== value)
				: [...collection, value]
		);
	};

	const handleResetFilters = () => {
		setSelectedStatuses([]);
	};

	const handleAcceptOffer = async (offer: Offer) => {
		if (offer.status !== 'pending') return;
		const confirmed = window.confirm(
			copy(
				`Accept this offer from ${offer.gardenerName}? A project will be created, this offer will be marked as accepted, and you will not be able to accept another offer for the same plan.`,
				`Dieses Angebot von ${offer.gardenerName} annehmen? Es wird ein Projekt erstellt, dieses Angebot als angenommen markiert und Sie können für denselben Plan kein weiteres Angebot annehmen.`,
				`Prihvatiti ovu ponudu od ${offer.gardenerName}? Kreirat će se projekt, ova ponuda će biti označena kao prihvaćena i nećete moći prihvatiti drugu ponudu za isti plan.`
			)
		);
		if (!confirmed) return;
		try {
			const offerIdNum = parseInt(offer.id, 10);
			let projectId: string | undefined;
			if (!isNaN(offerIdNum)) {
				// Use backend API
				await acceptOfferAPI(offerIdNum);
				// Get project id for notification deep link (backend creates project on accept)
				try {
					const { project_id } = await ensureProjectForOffer(offerIdNum);
					projectId = String(project_id);
				} catch {
					// non-fatal
				}
			} else {
				// Fallback to localStorage
				acceptOfferLocal(offer.id);
			}
			
			// Notify the gardener that their offer was accepted (with project link when available)
			const plan = planId ? getPublicPlanById(planId) : null;
			if (plan) {
				notifyOfferAccepted(offer.gardenerId, offer.id, plan.id, plan.title, projectId).catch(err => {
					console.error('Failed to send notification:', err);
				});
				
				// Create project from accepted offer (other offers stay pending; seeker can accept more)
				try {
					createProjectFromOffer(offer, plan);
				} catch (projectErr) {
					console.error('Failed to create project from offer:', projectErr);
				}
			}
			
			// Reload offers to reflect changes
			if (planId) {
				if (!isNaN(resolvedPlanIdNum)) {
					const backendOffers = await getOffersForPlanAPI(resolvedPlanIdNum);
					setOffers(backendOffers.map(mapBackendOfferToFrontend));
				} else {
					setOffers(getOffersForPlanLocal(planId));
				}
			}
		} catch (err) {
			console.error('Failed to accept offer:', err);
			alert(copy('Failed to accept offer. Please try again.', 'Angebot konnte nicht angenommen werden. Bitte versuchen Sie es erneut.', 'Prihvaćanje ponude nije uspjelo. Pokušajte ponovno.'));
		}
	};

	const handleRejectOffer = async (offer: Offer) => {
		if (offer.status !== 'pending') return;
		try {
			const offerIdNum = parseInt(offer.id, 10);
			if (!isNaN(offerIdNum)) {
				// Use backend API
				await rejectOfferAPI(offerIdNum);
			} else {
				// Fallback to localStorage
				rejectOfferLocal(offer.id);
			}
			
			// Notify the gardener that their offer was rejected
			const plan = planId ? getPublicPlanById(planId) : null;
			if (plan) {
				notifyOfferRejected(offer.gardenerId, offer.id, plan.id, plan.title).catch(err => {
					console.error('Failed to send rejection notification:', err);
				});
			}
			
			// Reload offers to reflect changes
			if (planId) {
				if (!isNaN(resolvedPlanIdNum)) {
					const backendOffers = await getOffersForPlanAPI(resolvedPlanIdNum);
					setOffers(backendOffers.map(mapBackendOfferToFrontend));
				} else {
					setOffers(getOffersForPlanLocal(planId));
				}
			}
		} catch (err) {
			console.error('Failed to reject offer:', err);
			alert(copy('Failed to reject offer. Please try again.', 'Angebot konnte nicht abgelehnt werden. Bitte versuchen Sie es erneut.', 'Odbijanje ponude nije uspjelo. Pokušajte ponovno.'));
		}
	};
	
	const toggleOfferExpansion = (offerId: string) => {
		const newExpanded = new Set(expandedOffers);
		if (newExpanded.has(offerId)) {
			newExpanded.delete(offerId);
		} else {
			newExpanded.add(offerId);
		}
		setExpandedOffers(newExpanded);
	};

	const openMessageModal = (offerId: string) => {
		window.dispatchEvent(new Event('app:clear-search-focus'));
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		document.querySelectorAll<HTMLInputElement>('input[type="search"]').forEach((input) => input.blur());
		setTimeout(() => {
			if (document.activeElement instanceof HTMLElement) {
				document.activeElement.blur();
			}
			document.querySelectorAll<HTMLInputElement>('input[type="search"]').forEach((input) => input.blur());
		}, 0);
		setMessageOfferId(offerId);
	};
	
		return (
		<>
			<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
				<BackButton fallbackPath="/app/seeker/plans" label={copy('Back to plan', 'Zurück zum Plan', 'Natrag na plan')} size="sm" />
				<div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
					<div>
						<h1 className="h3 mb-0">{copy('Offers', 'Angebote', 'Ponude')}</h1>
						{planId && (() => {
							const plan = getPublicPlanById(planId);
							if (plan?.questionnaire?.projectStartDate) {
								return (
									<div className="small text-muted mt-1">
										<i className="bi bi-calendar3 me-1"></i>
										{copy('Your preferred start:', 'Ihr gewünschter Start:', 'Vaš željeni početak:')} <strong>{new Date(plan.questionnaire.projectStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
									</div>
								);
							}
							return null;
						})()}
					</div>
					<div className="d-flex flex-wrap gap-2 align-items-center">
						<input
							type="search"
							className="form-control"
							placeholder={copy('Search gardener, notes, timeline...', 'Gärtner, Notizen, Zeitplan suchen...', 'Pretraži vrtlara, bilješke, vremenski plan...')}
							value={searchTerm}
							onChange={(event) => setSearchTerm(event.target.value)}
						/>
						<select
							className="form-select form-select-sm"
							style={{ width: 'auto' }}
							value={sortBy}
							onChange={(e) => setSortBy(e.target.value as 'newest' | 'price-low' | 'price-high')}
							aria-label={copy('Sort offers', 'Angebote sortieren', 'Sortiraj ponude')}
						>
							<option value="newest">{copy('Newest first', 'Neueste zuerst', 'Najnovije prvo')}</option>
							<option value="price-low">{copy('Price (low-high)', 'Preis (niedrig-hoch)', 'Cijena (niža-viša)')}</option>
							<option value="price-high">{copy('Price (high-low)', 'Preis (hoch-niedrig)', 'Cijena (viša-niža)')}</option>
						</select>
						<FilterToggle
							isOpen={isFiltersOpen}
							onToggle={() => setIsFiltersOpen((prev) => !prev)}
							activeCount={activeFiltersCount}
							buttonProps={{ className: 'btn btn-outline-secondary' }}
						/>
					</div>
				</div>
			</div>
			<FiltersPanel
				isOpen={isFiltersOpen}
				onApply={() => setIsFiltersOpen(false)}
				onReset={handleResetFilters}
			>
				<div className="row g-4">
					<div className="col-12">
						<div className="mb-2 text-muted text-uppercase small">{copy('Status', 'Status', 'Status')}</div>
						{(['pending', 'accepted', 'rejected'] as const).map((status) => (
							<div className="form-check" key={status}>
								<input
									className="form-check-input"
									type="checkbox"
									id={`offers-status-${status}`}
									checked={selectedStatuses.includes(status)}
									onChange={() =>
										toggleSelection(status, selectedStatuses, setSelectedStatuses)
									}
								/>
								<label className="form-check-label" htmlFor={`offers-status-${status}`}>
									{status === 'pending'
										? copy('Pending', 'Ausstehend', 'Na čekanju')
										: status === 'accepted'
											? copy('Accepted', 'Angenommen', 'Prihvaćeno')
											: copy('Rejected', 'Abgelehnt', 'Odbijeno')}
								</label>
							</div>
						))}
					</div>
				</div>
			</FiltersPanel>
			{error && (
				<div className="alert alert-warning mb-3">
					{error}
				</div>
			)}
			{isLoading ? (
				<div className="card">
					<div className="card-body text-center py-5">
						<div className="spinner-border text-success" role="status">
							<span className="visually-hidden">{copy('Loading...', 'Lädt...', 'Učitavanje...')}</span>
						</div>
						<p className="text-muted mt-3 mb-0">{copy('Loading offers...', 'Angebote werden geladen...', 'Učitavanje ponuda...')}</p>
					</div>
				</div>
			) : (
			<div className="card offers-table-card">
				<div className="card-body p-0 offers-table-responsive">
					<table className="table align-middle offers-table mb-0">
						<thead>
							<tr>
								<th className="offers-col-gardener ps-3">{copy('Gardener', 'Gärtner', 'Vrtlar')}</th>
								<th className="offers-col-estimated">{copy('Estimated', 'Geschätzt', 'Procijenjeno')}</th>
								<th className="d-none d-xl-table-cell">{copy('VAT', 'MwSt.', 'PDV')} 19%</th>
								<th className="d-none d-lg-table-cell offers-col-notes">{copy('Notes', 'Notizen', 'Bilješke')}</th>
								<th className="offers-col-timeline">{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</th>
								<th className="d-none d-xl-table-cell">{copy('Start Date', 'Startdatum', 'Datum početka')}</th>
								<th className="d-none d-md-table-cell">{copy('Valid until', 'Gültig bis', 'Vrijedi do')}</th>
								<th className="d-none d-lg-table-cell">{copy('Created', 'Erstellt', 'Kreirano')}</th>
								<th>{copy('Status', 'Status', 'Status')}</th>
								<th className="offers-col-actions pe-3"></th>
							</tr>
						</thead>
						<tbody>
							{filteredOffers.map(o => {
								const isExpanded = expandedOffers.has(o.id);
								return (
									<>
										<tr key={o.id} className={o.status === 'rejected' ? 'table-danger-subtle' : undefined}>
											<td className="ps-3 offers-col-gardener">
												<div className="d-flex align-items-center gap-2">
													<button
														type="button"
														className="btn btn-sm btn-link p-0"
														onClick={() => toggleOfferExpansion(o.id)}
														style={{ minWidth: '24px' }}
													>
														<i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
													</button>
													<div className="d-flex flex-column gap-1">
														<span className="fw-medium">{o.gardenerName}</span>
														{o.selectedGardeners && o.selectedGardeners.length > 0 && (
															<div className="small text-muted">
																<i className="bi bi-people me-1"></i>
																{o.selectedGardeners.length}{' '}
																{o.selectedGardeners.length !== 1
																	? copy('gardeners assigned', 'Gärtner zugewiesen', 'vrtlara dodijeljeno')
																	: copy('gardener assigned', 'Gärtner zugewiesen', 'vrtlar dodijeljen')}
															</div>
														)}
														<StarRating companyName={o.gardenerName} size="sm" />
													</div>
												</div>
											</td>
											<td className="offers-col-estimated">{(o.bands.mid || o.bands.min).toLocaleString()} €</td>
											<td className="d-none d-xl-table-cell">19%</td>
											<td className="d-none d-lg-table-cell offers-col-notes">
												<span className="small text-muted text-truncate d-block">{o.message || '—'}</span>
											</td>
											<td className="offers-col-timeline">
												<span className="small text-truncate d-block">{o.timelineEstimate || '—'}</span>
											</td>
											<td className="d-none d-xl-table-cell">
												{o.projectStartDate ? (
													<span className="small">
														{new Date(o.projectStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
													</span>
												) : (
													<span className="small text-muted">—</span>
												)}
											</td>
											<td className="d-none d-md-table-cell">
												{o.validUntil ? (
													<span className="small">
														{new Date(o.validUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
													</span>
												) : (
													<span className="small text-muted">—</span>
												)}
											</td>
											<td className="d-none d-lg-table-cell">{new Date(o.createdAt).toLocaleDateString('en-GB')}</td>
											<td>
												<span
													className={`badge ${
														o.status === 'accepted'
															? 'text-bg-success'
															: o.status === 'rejected'
																? 'text-bg-danger'
																: 'text-bg-warning'
													}`}
												>
													{o.status === 'accepted'
														? copy('Accepted', 'Angenommen', 'Prihvaćeno')
														: o.status === 'rejected'
															? copy('Rejected', 'Abgelehnt', 'Odbijeno')
															: copy('Pending', 'Ausstehend', 'Na čekanju')}
												</span>
											</td>
											<td className="offers-col-actions pe-3">
												<div className="offers-actions">
													<button
														type="button"
														className="btn btn-sm btn-outline-primary"
														onClick={(e) => { e.stopPropagation(); openMessageModal(o.id); }}
														title={copy('Message gardener', 'Gärtner schreiben', 'Pošalji poruku vrtlaru')}
													>
														<i className="bi bi-chat-dots me-1" aria-hidden></i>
														{copy('Message', 'Nachricht', 'Poruka')}
													</button>
													<button
														className="btn btn-sm btn-success"
														onClick={() => handleAcceptOffer(o)}
														disabled={o.status !== 'pending'}
													>
														{copy('Accept', 'Annehmen', 'Prihvati')}
													</button>
													<button
														className="btn btn-sm btn-outline-secondary"
														onClick={() => handleRejectOffer(o)}
														disabled={o.status !== 'pending'}
													>
														{copy('Reject', 'Ablehnen', 'Odbij')}
													</button>
												</div>
											</td>
										</tr>
										{isExpanded && (
											<tr key={`${o.id}-timeline`}>
												<td colSpan={10} className="offers-expanded-cell">
													<div className="offers-expanded-detail">
														<OfferDetailPreview
															offer={o}
															plan={planForOffers ?? (planId ? getPublicPlanById(planId) ?? null : null)}
															viewAs="seeker"
															planId={planId}
														/>
													</div>
												</td>
											</tr>
										)}
									</>
								);
							})}
							{filteredOffers.length === 0 && (
								<tr>
									<td colSpan={10} className="text-muted text-center py-4">
										{offers.length === 0
											? copy('No offers yet.', 'Noch keine Angebote.', 'Još nema ponuda.')
											: copy('No offers match your filters yet.', 'Keine Angebote passen zu Ihren Filtern.', 'Nijedna ponuda ne odgovara vašim filtrima.')}
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>
			)}
			{messageOfferId && user && (() => {
				const offerIdNum = parseInt(messageOfferId, 10);
				const userIdNum =
					planOwnerSeekerIdNum ??
					(typeof user.id === 'string' ? parseInt(user.id, 10) : user.id);
				const offer = offers.find((x) => x.id === messageOfferId);
				if (isNaN(offerIdNum) || userIdNum == null || isNaN(userIdNum)) return null;
				return (
					<>
						<div className="modal fade show" style={{ display: 'block', zIndex: 20000 }} role="dialog" aria-modal="true" aria-labelledby="messageModalTitle">
							<div className="modal-dialog modal-dialog-centered">
								<div className="modal-content">
									<div className="modal-header">
										<h5 className="modal-title" id="messageModalTitle">
											{copy('Message', 'Nachricht', 'Poruka')} {offer?.gardenerName ?? copy('gardener', 'Gärtner', 'vrtlar')}
										</h5>
										<button type="button" className="btn-close" aria-label="Close" onClick={() => setMessageOfferId(null)} />
									</div>
									<div className="modal-body">
										<ConversationThread
											variant="offer"
											offerId={offerIdNum}
											currentUserId={userIdNum}
											otherPartyName={offer?.gardenerName}
											onClose={() => setMessageOfferId(null)}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-backdrop fade show" style={{ zIndex: 19990 }} />
					</>
				);
			})()}
		</>
	);
}


